import { useAuth } from "../../lib/auth";
import { Link, NavLink, useParams } from "react-router-dom";
import { AlertTriangle, Wrench, FileText, Inbox } from "lucide-react";
import { useApi, PageHeader, Panel, Stat, Loading, Empty } from "../../components/kit";
import People from "./People";
import CourseMap from "./CourseMap";
import Courses from "./Courses";
import AdminPolicies from "./AdminPolicies";
import AdminDocuments from "./AdminDocuments";
import Settings from "./Settings";
import Audit from "./Audit";

const TABS = [["", "Overview"], ["people", "People"], ["course-map", "Roles & course map"], ["courses", "Courses"], ["policies", "Policies"], ["documents", "Documents"], ["settings", "Settings & links"], ["audit", "Audit log"]];

function Overview() {
  const { data: d, loading } = useApi("/admin/overview");
  if (loading) return <Loading />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Active people" value={d.people.active} testid="ov-active" />
        <Stat label="Training overdue" value={d.training.overdue} tone="text-rose-700" testid="ov-overdue" />
        <Stat label="Training in progress" value={d.training.in_progress + (d.training.awaiting_signoff || 0)} testid="ov-in-progress" />
        <Stat label="Completions" value={d.training.completed} tone="text-emerald-700" testid="ov-completed" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title={<span className="flex items-center gap-2"><Inbox className="h-5 w-5" /> Review queues</span>} testid="ov-queues">
          <ul className="space-y-2">
            {[["Certificates awaiting review", d.queues.certificates], ["SDS awaiting approval", d.queues.sds], ["Open safety reports", d.queues.safety]].map(([l, n]) => (
              <li key={l}><Link to="/reviews" className="flex justify-between rounded-lg border bg-white px-4 py-3 hover:border-[color:var(--ink)]"><span>{l}</span><b>{n}</b></Link></li>))}
          </ul>
        </Panel>
        <Panel title={<span className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> People without a manager ({d.people.no_manager.length})</span>} testid="ov-no-manager">
          {d.people.no_manager.length === 0 ? <Empty>Everyone has a manager.</Empty> : (
            <ul className="divide-y">{d.people.no_manager.map((p) => <li key={p.id} className="py-2 flex justify-between"><span>{p.name}</span><span className="text-sm text-muted-foreground">{p.department}</span></li>)}</ul>)}
          <Link to="/admin/people" className="mt-3 inline-block text-sm font-semibold text-[color:var(--dcbc-red)]">Assign managers</Link>
        </Panel>
        <Panel title={<span className="flex items-center gap-2"><FileText className="h-5 w-5" /> Draft or unpublished content</span>} testid="ov-drafts">
          {d.draft_courses.length === 0 ? <Empty>No drafts.</Empty> : (
            <ul className="divide-y">{d.draft_courses.map((c) => <li key={c.id}><Link to={`/admin/courses/${c.id}`} className="flex justify-between py-2 hover:underline"><span>{c.title}</span><span className="text-sm text-muted-foreground">{c.published ? "New version in draft" : "Never published"}</span></Link></li>)}</ul>)}
        </Panel>
        <Panel title={<span className="flex items-center gap-2"><Wrench className="h-5 w-5" /> Setup tasks ({d.setup_tasks.length})</span>} testid="ov-setup">
          {d.setup_tasks.length === 0 ? <Empty>All set up.</Empty> : <ul className="list-disc pl-5 space-y-1 text-sm">{d.setup_tasks.map((t) => <li key={t}>{t}</li>)}</ul>}
        </Panel>
      </div>
    </div>
  );
}

function EditorAdmin({ tab }) {
  const C = tab === "course-map" ? CourseMap : Courses;
  return (
    <div>
      <PageHeader eyebrow="Course editor" title="Course builder" testid="admin-header" />
      <nav className="mb-8 flex gap-1 overflow-x-auto border-b" aria-label="Course builder">
        {[["courses", "Courses"], ["course-map", "Course map"]].map(([k, l]) => (
          <NavLink key={k} to={`/admin/${k}`} data-testid={`admin-tab-${k}`}
            className={() => `tap shrink-0 inline-flex items-center border-b-2 px-4 text-sm font-semibold ${(C === CourseMap) === (k === "course-map") ? "border-[color:var(--dcbc-red)] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</NavLink>
        ))}
      </nav>
      <C />
    </div>
  );
}

export default function Admin() {
  const { tab = "" } = useParams();
  const { user } = useAuth();
  if (!user.is_superadmin) return <EditorAdmin tab={tab} />;
  const C = { "": Overview, people: People, "course-map": CourseMap, courses: Courses, policies: AdminPolicies, documents: AdminDocuments, settings: Settings, audit: Audit }[tab] || Overview;
  return (
    <div>
      <PageHeader eyebrow="Superadmin" title="Administration" testid="admin-header" />
      <nav className="mb-8 flex gap-1 overflow-x-auto border-b" aria-label="Administration">
        {TABS.map(([k, l]) => (
          <NavLink key={k} to={`/admin${k ? `/${k}` : ""}`} end data-testid={`admin-tab-${k || "overview"}`}
            className={({ isActive }) => `tap shrink-0 inline-flex items-center border-b-2 px-4 text-sm font-semibold ${isActive ? "border-[color:var(--dcbc-red)] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</NavLink>
        ))}
      </nav>
      <C />
    </div>
  );
}
